import {
  Alert,
  Box,
  Chip,
  Divider,
  Paper,
  Stack,
  Typography,
} from "@mui/material";
import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import { useCRUD } from "../../../api/crud";
import { useBreadcrumbs } from "../../../components/ui/breadcrumps/breadcrumbsContext";
import CustomCircularProgress from "../../../components/ui/customCircularProgress/customCircularProgress.component";
import {
  KSEF_INVOICE_TYPE_LABEL_KEYS,
  PURCHASE_INVOICES_ENDPOINT,
} from "../../../consts";
import { useHasPermission } from "../../../contexts/auth";
import { PurchaseInvoice } from "../../../types/invoices";
import { formatNumber } from "../../../utils";

// Identifier types KSeF reports for the buyer; the rest carry no prefix.
const BUYER_IDENTIFIER_PREFIXES: Record<string, string> = {
  Nip: "NIP",
  VatUe: "VAT UE",
};

const Field = ({ label, value }: { label: string; value: React.ReactNode }) => (
  <Box sx={{ mb: 1 }}>
    <Typography variant="caption" color="text.secondary" display="block">
      {label}
    </Typography>
    <Typography variant="body2">{value || "—"}</Typography>
  </Box>
);

const formatMoment = (value: string | null) =>
  value ? new Date(value).toLocaleString() : "";

export default function ViewPurchaseInvoice() {
  const { t } = useTranslation();
  const { id } = useParams();
  const { get } = useCRUD(PURCHASE_INVOICES_ENDPOINT);
  const { setOverride } = useBreadcrumbs();
  const [invoice, setInvoice] = useState<PurchaseInvoice | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const canViewPurchaseInvoices = useHasPermission(
    "invoice.view_purchaseinvoice"
  );

  const fetchInvoice = useCallback(async () => {
    if (!id) {
      return;
    }
    const response = await get(id);
    const fetched = response?.data ?? null;
    setInvoice(fetched);
    setLoading(false);
    setOverride(`/invoices/purchase/${id}`, fetched ? fetched.invoice_number : "");
  }, [id, get, setOverride]);

  useEffect(() => {
    fetchInvoice();
  }, [fetchInvoice]);

  if (!canViewPurchaseInvoices) {
    return null;
  }

  if (loading) {
    return <CustomCircularProgress />;
  }

  if (!invoice) {
    return <Typography>{t("Purchase invoice not found")}</Typography>;
  }

  const buyerIdentifierPrefix =
    BUYER_IDENTIFIER_PREFIXES[invoice.buyer_identifier_type];

  return (
    <Stack spacing={2} sx={{ p: 2 }}>
      <Stack
        direction="row"
        spacing={1}
        alignItems="center"
        flexWrap="wrap"
        useFlexGap
      >
        <Typography variant="h5" component="h1" sx={{ fontWeight: 600 }}>
          {t("Purchase invoice")} - {invoice.invoice_number}
        </Typography>
        <Chip
          size="small"
          label={t(
            KSEF_INVOICE_TYPE_LABEL_KEYS[invoice.invoice_type] ??
              invoice.invoice_type
          )}
        />
        {invoice.has_attachment && (
          <Chip size="small" color="info" label={t("Has attachment")} />
        )}
        {invoice.is_self_invoicing && (
          <Chip size="small" label={t("Self-invoicing")} />
        )}
      </Stack>

      <Alert severity="success">
        {t("KSeF number")}: {invoice.ksef_number}
      </Alert>

      <Stack direction={{ xs: "column", md: "row" }} spacing={2}>
        <Paper variant="outlined" sx={{ p: 2, flex: 1 }}>
          <Typography variant="overline" color="text.secondary">
            {t("Seller")}
          </Typography>
          <Typography variant="subtitle2">{invoice.seller_name}</Typography>
          <Typography variant="body2">NIP: {invoice.seller_nip}</Typography>
        </Paper>
        <Paper variant="outlined" sx={{ p: 2, flex: 1 }}>
          <Typography variant="overline" color="text.secondary">
            {t("Buyer")}
          </Typography>
          <Typography variant="subtitle2">{invoice.buyer_name}</Typography>
          {invoice.buyer_identifier && (
            <Typography variant="body2">
              {buyerIdentifierPrefix
                ? `${buyerIdentifierPrefix}: ${invoice.buyer_identifier}`
                : invoice.buyer_identifier}
            </Typography>
          )}
        </Paper>
      </Stack>

      <Stack direction={{ xs: "column", sm: "row" }} spacing={4}>
        <Field label={t("Date of issue")} value={invoice.issued_at} />
        <Field
          label={t("Received by KSeF at")}
          value={formatMoment(invoice.invoicing_date)}
        />
        <Field
          label={t("Stored in KSeF at")}
          value={formatMoment(invoice.permanent_storage_date)}
        />
        <Field label={t("Invoicing mode")} value={invoice.invoicing_mode} />
      </Stack>

      <Divider />

      <Stack direction={{ xs: "column", md: "row" }} justifyContent="flex-end">
        <Paper variant="outlined" sx={{ p: 2, minWidth: 260 }}>
          <Stack direction="row" justifyContent="space-between" spacing={2}>
            <Typography variant="body2" color="text.secondary">
              {t("Net total")}
            </Typography>
            <Typography variant="body2">
              {formatNumber(invoice.net_amount)} {invoice.currency}
            </Typography>
          </Stack>
          {/* KSeF reports the VAT total in PLN whatever the invoice currency. */}
          <Stack direction="row" justifyContent="space-between" spacing={2}>
            <Typography variant="body2" color="text.secondary">
              {t("VAT total")}
            </Typography>
            <Typography variant="body2">
              {formatNumber(invoice.vat_amount)} PLN
            </Typography>
          </Stack>
          <Divider sx={{ my: 1 }} />
          <Stack direction="row" justifyContent="space-between" spacing={2}>
            <Typography variant="subtitle2">{t("Gross value")}</Typography>
            <Typography variant="subtitle2">
              {formatNumber(invoice.gross_amount)} {invoice.currency}
            </Typography>
          </Stack>
        </Paper>
      </Stack>
    </Stack>
  );
}
